import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { type FeeFormData } from './insuranceFormShared'

interface Props {
  value: FeeFormData[]
  onChange: (next: FeeFormData[]) => void
  /** Called with the id of an existing (persisted) fee removed from the list. */
  onRemoveExisting?: (id: string) => void
}

export function OneTimeFeesFieldset({ value, onChange, onRemoveExisting }: Props) {
  const update = (index: number, field: 'label' | 'amount', fieldValue: string) =>
    onChange(value.map((f, i) => (i === index ? { ...f, [field]: fieldValue } : f)))

  const add = () => onChange([...value, { isNew: true, label: '', amount: '0' }])

  const remove = (index: number) => {
    const fee = value[index]
    if (fee.id && !fee.isNew) onRemoveExisting?.(fee.id)
    onChange(value.filter((_, i) => i !== index))
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">One-time fees</h3>
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus className="mr-1 h-4 w-4" />
          Add fee
        </Button>
      </div>
      <div className="space-y-3">
        {value.map((fee, index) => (
          <div key={fee.id ?? `new-fee-${index}`} className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
            <div>
              <Label className="text-muted-foreground text-xs">Label</Label>
              <Input
                value={fee.label}
                onChange={(e) => update(index, 'label', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Amount</Label>
              <div className="mt-1">
                <CurrencyInput
                  value={fee.amount === '' ? null : Number(fee.amount)}
                  onChange={(v) => update(index, 'amount', v == null ? '' : String(v))}
                />
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Remove fee"
              onClick={() => remove(index)}
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {value.length === 0 && (
          <p className="text-muted-foreground py-2 text-center text-sm">No one-time fees.</p>
        )}
      </div>
    </div>
  )
}
